import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { PrinciplesConfigGrid } from 'src/screens/administration/configuration-management/principles-configuration/principles/PrinciplesConfigGrid'
import { ConfigurationTableSettings, DEFAULT_TABLE_SETTINGS } from 'src/shared/constants/table-settings-constants'
import { useClient } from 'urql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useCallback, useEffect, useState } from 'react'
import { Query_Root } from 'src/@types/graphql'
import { queryPrincipleConfigsAndAggregateCount } from 'src/screens/administration/configuration-management/principles-configuration/principles/principlesConfigQueries'

export const PrinciplesConfigurationPrinciplesPage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const notificationService = useNotificationService()

  const [tableSettings, setTableSettings] = useState<ConfigurationTableSettings>(DEFAULT_TABLE_SETTINGS)

  const fetchRows = useCallback(async () => {
    const limit = tableSettings.paginationModel.pageSize
    const offset = tableSettings.paginationModel.page * tableSettings.paginationModel.pageSize

    const { data, error } = await urqlClient
      .query<
        {
          principle_config: Query_Root['principle_config']
          principle_config_aggregate: Query_Root['principle_config_aggregate']
        },
        {
          offset: number
          limit: number
        }
      >(queryPrincipleConfigsAndAggregateCount, {
        offset,
        limit,
      })
      .toPromise()

    if (error || !data?.principle_config || !data.principle_config_aggregate) {
      throw new Error('Resource not found')
    }

    const fetchedRows = data.principle_config.map((pc) => ({
      id: pc.id,
      name: pc.principle_config_name_locale,
      status: pc.active,
    }))
    const fetchedRowsCount = data.principle_config_aggregate.aggregate?.count ?? 0

    return {
      fetchedRows,
      fetchedRowsCount,
    }
  }, [tableSettings.paginationModel, urqlClient])

  useEffect(() => {
    setTableSettings((prevSettings) => ({ ...prevSettings, gridLoading: true }))
    fetchRows()
      .then((res) => {
        setTableSettings((prevSettings) => ({
          ...prevSettings,
          rows: res.fetchedRows,
          rowsCount: res.fetchedRowsCount,
        }))
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
      .finally(() => {
        setTableSettings((prevSettings) => ({ ...prevSettings, gridLoading: false }))
      })
  }, [fetchRows, notificationService])

  return (
    <ScreenLayout
      title={`${getMessage('label.navigation.configuration.principles')} ${getMessage('label.configuration')}`}
    >
      <PageLayout>
        <PrinciplesConfigGrid tableSettings={tableSettings} setTableSettings={setTableSettings} />
      </PageLayout>
    </ScreenLayout>
  )
}
